import Skeleton from '@mui/material/Skeleton';
// eslint-disable-next-line import/named
import { AxisConfig, CurveType, legendClasses, LineSeriesType } from '@mui/x-charts';
import { LineChart } from '@mui/x-charts/LineChart';
import { MakeOptional } from '@mui/x-charts/models/helpers';
import NoData from 'components/elements/Reports/NoData';
import * as React from 'react';
import { colours } from 'utils/constants';

interface OccupancyGraphProps {
  dates: Date[];
  occupants: number[];
  capacity: number | undefined;
  loading: boolean;
  showMaxCapacity: boolean;
}

export default function OccupancyGraph({
  dates,
  occupants,
  capacity,
  loading,
  showMaxCapacity
}: OccupancyGraphProps) {
  const GRAPH_HEIGHT = 300;

  if (loading) {
    return <Skeleton variant="rectangular" height={GRAPH_HEIGHT} />;
  }

  if (!dates || !occupants || !dates.length || !occupants.length) {
    return (
      <NoData
        height={GRAPH_HEIGHT}
        hint="Consider updating your building, space, and date filters"
      />
    );
  }

  // Create an array of occupancy percentages
  const occupantsRelative = occupants.map((occupant) => (100 * occupant) / (capacity || 1));

  const series: MakeOptional<LineSeriesType, 'type'>[] = [
    {
      id: 'occupants',
      label: 'Occupants',
      data: occupants,
      area: true,
      curve: 'stepAfter' as CurveType,
      showMark: false,
      color: colours.seafoam,
      yAxisKey: 'absolute'
    }
  ];

  const yAxis: MakeOptional<AxisConfig, 'id'>[] | undefined = [
    {
      id: 'absolute',
      data: occupants,
      label: 'Occupants',
      valueFormatter: (numOccupants: number) => `${numOccupants}`
    }
  ];

  if (showMaxCapacity) {
    yAxis.push({
      id: 'relative',
      data: occupants,
      label: '',
      valueFormatter: (occupancy: number) => `${occupancy}%`,
      max: showMaxCapacity
        ? Math.max(100, Math.floor(100 * Math.max(...occupants)) / (capacity || 1))
        : Math.floor((100 * Math.max(...occupants)) / (capacity || 1))
    });

    const capacityValues = new Array(dates.length).fill(capacity);
    series.push({
      id: 'capacity',
      label: 'Max capacity',
      data: capacityValues,
      curve: 'linear' as CurveType,
      showMark: false,
      color: 'red',
      yAxisKey: 'absolute',
      valueFormatter: (maxCapacity: number) => `${maxCapacity}`
    });

    series.push({
      id: 'occupants-relative',
      label: 'Space occupancy',
      data: occupantsRelative,
      curve: 'stepAfter' as CurveType,
      showMark: false,
      color: 'transparent',
      yAxisKey: 'relative',
      valueFormatter: (occupancy: number) => `${(occupancy ?? 0).toFixed(1)}%`
    });
  }

  return (
    <div style={{ marginLeft: 20 }}>
      <LineChart
        sx={{
          // area fill
          '& .MuiAreaElement-series-occupants': {
            fill: "url('#lineGradient')"
          },
          // legend
          [`& .${legendClasses.root}`]: {
            display: 'none'
          },
          // line style
          '& .MuiLineElement-root': {
            strokeWidth: 1
          },
          // bottomAxis Line Styles
          '& .MuiChartsAxis-bottom .MuiChartsAxis-line': {
            stroke: colours.axisGrey,
            strokeWidth: 0.4
          },
          // leftAxis Line Styles
          '& .MuiChartsAxis-left .MuiChartsAxis-line': {
            stroke: colours.axisGrey,
            strokeWidth: 0.4
          },
          // rightAxis Line Styles
          '& .MuiChartsAxis-right .MuiChartsAxis-line': {
            stroke: colours.axisGrey,
            strokeWidth: 0.4
          },
          // tick colour
          '& .MuiChartsAxis-tick': {
            stroke: colours.axisGrey,
            strokeWidth: 0.4
          },
          // axis label colour
          '& .MuiChartsAxis-root .MuiChartsAxis-label': {
            fill: colours.axisGrey
          },
          // change all labels fontFamily shown on both xAxis and yAxis
          '& .MuiChartsAxis-tickContainer .MuiChartsAxis-tickLabel': {
            fill: colours.axisGrey
          },
          // axis label positioning
          '& .MuiChartsAxis-left .MuiChartsAxis-label': {
            transform: 'translate(-11px, 0)'
          }
        }}
        xAxis={[
          {
            data: dates,
            scaleType: 'time',
            label: 'Date'
          }
        ]}
        yAxis={yAxis}
        series={series}
        leftAxis="absolute"
        rightAxis={showMaxCapacity ? 'relative' : undefined}
        height={GRAPH_HEIGHT}
      >
        <defs>
          <linearGradient id="lineGradient" gradientTransform="rotate(90)">
            <stop offset="1%" stopColor={colours.darkTeal} />
            <stop offset="99%" stopColor={colours.lightTeal} />
          </linearGradient>
        </defs>
      </LineChart>
    </div>
  );
}
